(() => {
  if (customElements.get('blisbond-home-slideshow')) return;
  const loadSwiper = () => {
    if (typeof window.loadCatalogSwiper === 'function') return window.loadCatalogSwiper();
    if (window.blisbondHomeSwiperPromise) return window.blisbondHomeSwiperPromise;
    window.blisbondHomeSwiperPromise = new Promise((resolve, reject) => {
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://cdn.jsdelivr.net/npm/swiper@12.0.3/swiper-bundle.min.css';
      document.head.append(css);
      if (window.Swiper) { resolve(window.Swiper); return; }
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/swiper@12.0.3/swiper-bundle.min.js';
      script.onload = () => resolve(window.Swiper);
      script.onerror = reject;
      document.head.append(script);
    });
    return window.blisbondHomeSwiperPromise;
  };
  class HomeSlideshow extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.querySelector('[data-play]')?.remove();
      this.decorateNavigation();
      this.hovered = false;
      this.editing = false;
      loadSwiper().then(Swiper => {
        if (!this.isConnected || signal.aborted) return;
        const count = this.querySelectorAll('[data-block-id]').length;
        const many = count > 1;
        const desktop = Math.min(Number(this.dataset.desktopSlides) || 1, count || 1);
        const mobile = Math.min(Number(this.dataset.mobileSlides) || 1, count || 1);
        this.slider = new Swiper(this.querySelector('.swiper'), {
          slidesPerView: mobile,
          spaceBetween: Number(this.dataset.mobileGap) || 0,
          breakpoints: { 750: { slidesPerView: desktop, spaceBetween: Number(this.dataset.gap) || 0 } },
          loop: this.dataset.loop === 'true' && count > Math.ceil(Math.max(desktop, mobile)),
          speed: this.motion.matches ? 0 : Number(this.dataset.speed),
          watchOverflow: true,
          grabCursor: many,
          autoplay: this.dataset.autoplay === 'true' && many ? { delay: Number(this.dataset.delay), disableOnInteraction: false } : false,
          navigation: { prevEl: this.querySelector('[data-prev]'), nextEl: this.querySelector('[data-next]') },
          pagination: { el: this.querySelector('.home-slideshow__pagination'), clickable: true, renderBullet: (i, cls) => `<button type="button" class="${cls}" aria-label="Go to slide ${i + 1}"></button>` },
          a11y: { enabled: true },
          on: { slideChange: swiper => this.syncSlides(swiper) }
        });
        this.syncSlides(this.slider);
        const controls = this.querySelector('.home-slideshow__controls');
        if (controls) controls.hidden = !many;
        this.addEventListener('mouseenter', () => { this.hovered = true; this.syncPlayback(); }, { signal });
        this.addEventListener('mouseleave', () => { this.hovered = false; this.syncPlayback(); }, { signal });
        this.addEventListener('focusin', () => this.syncPlayback(), { signal });
        this.addEventListener('focusout', () => queueMicrotask(() => this.syncPlayback()), { signal });
        document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal });
        this.motion.addEventListener('change', () => { this.slider.params.speed = this.motion.matches ? 0 : Number(this.dataset.speed); this.syncPlayback(); }, { signal });
        this.addEventListener('shopify:block:select', event => {
          const slides = [...this.querySelectorAll('[data-block-id]')];
          const target = slides.find(s => s.dataset.blockId === event.detail.blockId);
          if (!target) return;
          this.editing = true;
          const index = Number(target.dataset.swiperSlideIndex ?? slides.indexOf(target));
          if (this.slider.params.loop) this.slider.slideToLoop(index, 0); else this.slider.slideTo(index, 0);
          this.syncPlayback();
        }, { signal });
        this.addEventListener('shopify:block:deselect', () => { this.editing = false; this.syncPlayback(); }, { signal });
        this.syncPlayback();
      }).catch(() => { this.dataset.loadFailed = 'true'; });
    }
    decorateNavigation() {
      const paths = {
        prev: 'M15 6s-6 4.419-6 6s6 6 6 6',
        next: 'M9 6s6 4.419 6 6s-6 6-6 6'
      };
      Object.entries(paths).forEach(([direction, path]) => {
        const button = this.querySelector('[data-' + direction + ']');
        if (!button) return;
        const label = button.getAttribute('aria-label') || button.textContent.trim();
        button.setAttribute('aria-label', label);
        button.title = label;
        button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="' + path + '"/></svg>';
      });
    }
    syncSlides(swiper) {
      swiper.slides.forEach((slide, i) => {
        const active = i === swiper.activeIndex;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
    }
    syncPlayback() {
      if (!this.slider || this.slider.destroyed) return;
      const stop = this.motion.matches || this.hovered || this.contains(document.activeElement) || document.hidden || this.editing || window.Shopify?.designMode;
      if (stop) this.slider.autoplay?.stop();
      else if (this.dataset.autoplay === 'true') this.slider.autoplay?.start();

    }
    disconnectedCallback() {
      this.abort?.abort();
      this.slider?.destroy(true, true);
      this.slider = null;
    }
  }
  customElements.define('blisbond-home-slideshow', HomeSlideshow);
})();